import React, { useMemo } from "react";
import { Box, BoxProps } from "@mui/material";
import DOMPurify from "dompurify";

interface SafeHtmlProps extends Omit<BoxProps, "children" | "dangerouslySetInnerHTML"> {
  html?: string | null;
  /** Rendered when `html` is empty, so callers don't repeat a `|| "-"` fallback. */
  fallback?: React.ReactNode;
}

/**
 * Renders stored email HTML (template bodies, sent notification bodies) as
 * formatted markup instead of escaped text.
 *
 * Always use this rather than `dangerouslySetInnerHTML` directly: template
 * bodies are authored as free-form HTML in a plain text field, so an author
 * could otherwise store `<img src=x onerror=...>` and run script in the
 * browser of everyone who later opens the preview. DOMPurify keeps the
 * formatting tags and drops scripts, event handlers and `javascript:` URLs.
 */
const SafeHtml: React.FC<SafeHtmlProps> = ({ html, fallback = "-", ...boxProps }) => {
  const clean = useMemo(
    () => (html ? DOMPurify.sanitize(html) : ""),
    [html],
  );

  // Sanitising can empty a string that was only ever markup (e.g. a bare
  // <script>), so decide on the cleaned result, not the raw input.
  if (!clean.trim()) return <Box {...boxProps}>{fallback}</Box>;

  return <Box {...boxProps} dangerouslySetInnerHTML={{ __html: clean }} />;
};

export default SafeHtml;
